<template>
  <!-- Face-off hero motif: two letterpress tiles (you vs opponent) meeting at a gold
       seam with a VS chip. Replaces the decorative "—— VS ——" hairline — the "versus"
       becomes a real object from the game and previews the head-to-head on arrival.
       Decorative: the player identity it echoes is announced elsewhere (the lobby's
       player-name row / the landing wordmark), so the whole motif is aria-hidden. -->
  <div class="fo-motif" aria-hidden="true" data-testid="faceoff-motif">
    <div class="fo-side">
      <span class="fo-tile fo-tile--you">{{ youInitial }}</span>
      <span class="fo-cap">{{ youLabel }}</span>
    </div>

    <div class="fo-mid">
      <span class="fo-hair"></span>
      <span class="fo-vs">VS</span>
      <span class="fo-hair"></span>
    </div>

    <div class="fo-side">
      <span class="fo-tile fo-tile--opp">{{ opponentInitial }}</span>
      <span class="fo-cap fo-cap--dim">{{ opponentLabel }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import STRINGS from '@/constants/strings';

withDefaults(
  defineProps<{
    youInitial?: string;
    youLabel?: string;
    opponentInitial?: string;
    opponentLabel?: string;
  }>(),
  {
    youInitial: 'Y',
    youLabel: STRINGS.auth.landing.faceoff.you,
    opponentInitial: '?',
    opponentLabel: STRINGS.auth.landing.faceoff.opponent,
  }
);
</script>

<style scoped>
.fo-motif {
  /* Bespoke letterpress tile browns — mid-tones between --color-arena-surface
     (#1a1612) and --color-arena-border (#3d3428); no global arena token maps
     to them, so they live here as local vars rather than raw hex mid-rule. */
  --fo-tile-hi: #2a2318;
  --fo-tile-lo: #1e1a12;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}
.fo-side {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.fo-tile {
  width: 64px;
  height: 64px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  font-family: var(--font-mono);
  font-weight: 700;
  font-size: 30px;
  color: var(--color-arena-text-bright);
  background: linear-gradient(180deg, var(--fo-tile-hi), var(--fo-tile-lo));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    inset 0 -2px 0 rgba(0, 0, 0, 0.3),
    0 2px 4px rgba(0, 0, 0, 0.4),
    0 0 8px rgba(201, 168, 76, 0.15);
}
.fo-tile--you {
  border: 2px solid var(--color-energy-primary);
}
.fo-tile--opp {
  border: 2px solid var(--color-energy-secondary);
  color: var(--color-arena-text-muted);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    inset 0 -2px 0 rgba(0, 0, 0, 0.3),
    0 2px 4px rgba(0, 0, 0, 0.4),
    0 0 8px rgba(91, 127, 164, 0.22);
}
.fo-cap {
  max-width: 88px;
  font-family: var(--font-mono);
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-energy-primary);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fo-cap--dim {
  color: var(--color-arena-text-muted);
}
.fo-mid {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 22px;
}
.fo-hair {
  width: 22px;
  height: 1.5px;
  background: linear-gradient(90deg, transparent, var(--color-energy-primary));
}
.fo-mid .fo-hair:last-child {
  background: linear-gradient(90deg, var(--color-energy-primary), transparent);
}
.fo-vs {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 14px;
  letter-spacing: 0.1em;
  color: var(--color-energy-primary);
  padding: 3px 9px;
  border: 1px solid var(--color-arena-border);
  border-radius: 20px;
  background: rgba(26, 22, 18, 0.85);
}

/* desktop bump — matches the design's larger desktop face-off */
@media (min-width: 1024px) {
  .fo-motif .fo-tile {
    width: 72px;
    height: 72px;
    font-size: 34px;
  }
  .fo-motif .fo-hair {
    width: 28px;
  }
}
</style>
